
import { Component, Mixins, Prop, Provide } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import { IState } from "@/store";
import {
  ICategoryDocsProviderData,
  ICategoryDocsProviderMethods
} from "@/models/providers/docs";
import { IProvider } from "@/models/providers";
import { IDocsCategory } from "@/models/docs";

@Component({
  computed: {
    ...mapState({
      categoryDocsScope(state: IState, getters) {
        return getters[`data/${DataModules.DOCUMENTATION}/categoryDocsScope`](
          this.categorySlug
        );
      },
      categoryDocs(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.DOCUMENTATION,
          scope: this.categoryDocsScope
        });
      }
    })
  }
})
export default class CategoryDocsProvider extends Mixins(RequestPromises)
  implements IProvider {
  // Props
  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({ required: true })
  public categorySlug!: string;

  @Prop({ default: null })
  public docsSlugs!: string[];

  // Map State

  public categoryDocsScope!: string;
  public categoryDocs!: IDocsCategory[];

  // Data
  public isLoading = false;
  public isReloading = false;
  public hasRequestError = false;

  // Lifecycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.isLoading = true;
    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.DOCUMENTATION,
      scope: this.categoryDocsScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    try {
      await this.getDocsCategories(this.isReloading);
      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public getDocsCategories(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.DOCUMENTATION}/listCategoryDocuments`,
      {
        categorySlug: this.categorySlug,
        scope: this.categoryDocsScope,
        vm: this,
        ignoreStored
      }
    );
  }

  public showDoc(docSlug: IDocsCategory["slug"]) {
    window.open(`https://docs.upmind.com/docs/${docSlug}`, "_blank");
  }

  // Computed

  public get selectedCategoryDocs(): IDocsCategory[] {
    let selectedDocs: IDocsCategory[] = [];

    if (!_.isNull(this.docsSlugs)) {
      _.forEach(this.docsSlugs, docSlug => {
        _.forEach(_.cloneDeep(this.categoryDocs), categoryDoc => {
          if (!docSlug.includes(".")) {
            if (categoryDoc.slug !== docSlug) return;
            selectedDocs.push(categoryDoc);
          } else {
            const [subCategorySlug, subCategoryDocSlug] = docSlug.split(".");

            if (categoryDoc.slug === subCategorySlug) {
              _.forEach(categoryDoc.children, subcategoryDoc => {
                if (subcategoryDoc.slug !== subCategoryDocSlug) return;

                const selectedCategory = _.find(selectedDocs, [
                  "slug",
                  subCategorySlug
                ]);

                if (!selectedCategory) {
                  categoryDoc.children = [subcategoryDoc];
                  selectedDocs.push(categoryDoc);
                  return;
                }

                if (
                  !_.some(selectedCategory.children, [
                    "slug",
                    subCategoryDocSlug
                  ])
                ) {
                  selectedDocs[
                    _.findIndex(selectedDocs, ["slug", subCategorySlug])
                  ].children.push(subcategoryDoc);
                }
              });
            }
          }
        });
      });
    } else {
      selectedDocs = this.categoryDocs;
    }

    return selectedDocs;
  }

  // Guarding

  public guardFunctionalities = ["list_documentation_categories"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Render
  public render(h: CreateElement) {
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $categoryDocsData: this.providerData(),
        $categoryDocsMethods: this.providerMethods
      })
    );
  }

  // Provide

  @Provide("categoryDocsProviderData")
  public providerData(): ICategoryDocsProviderData {
    return {
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      hasRequestError: this.hasRequestError,
      categoryDocs: this.categoryDocs,
      selectedCategoryDocs: this.selectedCategoryDocs
    };
  }

  // Provide Methods
  @Provide("categoryDocsProviderMethods")
  public providerMethods: ICategoryDocsProviderMethods = {
    reloadData: this.reloadData,
    showDoc: this.showDoc
  };
}
